import { useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { CalendarIcon, Check, Loader2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { EQUIPMENT_OPTIONS, SERVICE_OPTIONS, WHATSAPP } from "../data/content";
import { Reveal, SectionHeading } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-[#181410] border border-amber-500/20 px-4 py-3 text-sm text-[#FBF9F5] placeholder:text-stone-500 focus:outline-none focus:border-amber-400/60 transition-colors duration-300 min-h-[44px]";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block mb-2 text-xs font-mono uppercase tracking-[0.18em] text-stone-400">{label}</span>
    {children}
  </label>
);

export const QuoteForm = () => {
  const [form, setForm] = useState({
    fullName: "", phone: "", email: "", locationCity: "", notes: "",
  });
  const [eventDate, setEventDate] = useState(null);
  const [serviceType, setServiceType] = useState("");
  const [equipment, setEquipment] = useState([]);
  const [sending, setSending] = useState(false);
  const [reference, setReference] = useState(null);

  const toggleEquipment = (opt) =>
    setEquipment((prev) => (prev.includes(opt) ? prev.filter((o) => o !== opt) : [...prev, opt]));

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!eventDate || !serviceType) {
      toast.error("Seleziona la data dell'evento e la tipologia di servizio.");
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(`${API}/quote-request`, {
        ...form,
        eventDate: format(eventDate, "yyyy-MM-dd"),
        serviceType,
        equipmentCategories: equipment,
      });
      setReference(res.data.reference);
      toast.success("Richiesta inviata con successo!");
    } catch (err) {
      toast.error("Si è verificato un errore. Riprova o scrivici su WhatsApp.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="preventivo" data-testid="quote-section" className="relative py-24 sm:py-32 bg-[#181410] overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_50%_0%,rgba(212,163,89,0.08),transparent)]" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid gap-14 lg:grid-cols-[1fr_1.2fr]">
        <div>
          <SectionHeading
            eyebrow="Preventivo Gratuito"
            title="Richiedi un Preventivo Personalizzato"
            subtitle="Seleziona le attrezzature o descrivi il tuo allestimento ideale. Rispondiamo entro 24 ore con proposta tecnica dettagliata."
            testId="quote-title"
          />
          <Reveal delay={0.3}>
            <div className="mt-10 p-6 border border-amber-500/20 bg-[#151310]">
              <p className="text-sm text-stone-300 leading-relaxed">
                Preferisci una risposta immediata? Scrivici direttamente su WhatsApp allegando foto della tua location.
              </p>
              <a
                data-testid="quote-whatsapp-cta"
                href={`https://wa.me/${WHATSAPP.phone}?text=${encodeURIComponent(WHATSAPP.message)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 inline-flex items-center gap-2.5 px-6 py-3 rounded-full bg-[#25D366] text-[#181410] text-sm font-semibold hover:brightness-110 transition-all duration-300"
              >
                <MessageCircle className="w-4 h-4" />
                Scrivici su WhatsApp
              </a>
              <p className="mt-5 text-xs font-mono text-stone-500">
                {WHATSAPP.displayPhone} • info@lmserviceevent.com
              </p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div className="backdrop-blur-2xl bg-[#262019]/95 border border-amber-500/25 shadow-[0_20px_50px_rgba(0,0,0,0.7)] p-7 sm:p-10">
            {reference ? (
              <div data-testid="quote-success-message" className="py-10 text-center">
                <span className="mx-auto flex w-14 h-14 items-center justify-center rounded-full border border-amber-400/50 bg-amber-400/10">
                  <Check className="w-6 h-6 text-amber-300" />
                </span>
                <h3 className="mt-6 text-2xl font-serif text-[#FBF9F5]">Richiesta ricevuta!</h3>
                <p className="mt-3 text-sm text-stone-400 leading-relaxed max-w-sm mx-auto">
                  Il tuo riferimento è <span className="text-amber-300 font-mono">{reference}</span>.
                  Ti ricontatteremo entro 24 ore con una proposta tecnica dettagliata.
                </p>
                <button
                  data-testid="quote-new-request-btn"
                  onClick={() => setReference(null)}
                  className="mt-8 px-6 py-3 rounded-full border border-amber-500/30 text-amber-300 text-sm hover:bg-amber-400/10 transition-colors"
                >
                  Invia un'altra richiesta
                </button>
              </div>
            ) : (
              <form data-testid="quote-form" onSubmit={submit} className="space-y-5">
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Nome e Cognome *">
                    <input data-testid="quote-fullname-input" required value={form.fullName} onChange={set("fullName")} placeholder="Maria Rossi" className={inputCls} />
                  </Field>
                  <Field label="Telefono / WhatsApp *">
                    <input data-testid="quote-phone-input" required type="tel" value={form.phone} onChange={set("phone")} placeholder="+39 333 000 0000" className={inputCls} />
                  </Field>
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Email *">
                    <input data-testid="quote-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="maria@esempio.it" className={inputCls} />
                  </Field>
                  <Field label="Data dell'Evento *">
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          data-testid="quote-date-input"
                          className={`${inputCls} flex items-center justify-between text-left ${!eventDate && "text-stone-500"}`}
                        >
                          {eventDate ? format(eventDate, "dd MMMM yyyy", { locale: it }) : "Seleziona data"}
                          <CalendarIcon className="w-4 h-4 text-amber-400/70" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-[#2d261b] border-amber-500/25" align="start">
                        <Calendar
                          mode="single"
                          selected={eventDate}
                          onSelect={setEventDate}
                          locale={it}
                          disabled={(d) => d < new Date()}
                          className="text-stone-200"
                        />
                      </PopoverContent>
                    </Popover>
                  </Field>
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Luogo o Location *">
                    <input data-testid="quote-location-input" required value={form.locationCity} onChange={set("locationCity")} placeholder="Villa / Città" className={inputCls} />
                  </Field>
                  <Field label="Tipologia di Servizio *">
                    <Select value={serviceType} onValueChange={setServiceType}>
                      <SelectTrigger data-testid="quote-servicetype-select" className={`${inputCls} rounded-none`}>
                        <SelectValue placeholder="Seleziona servizio" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#2d261b] border-amber-500/25 text-stone-200">
                        {SERVICE_OPTIONS.map((o) => (
                          <SelectItem key={o} value={o} className="focus:bg-amber-400/15 focus:text-amber-200">
                            {o}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
                <div>
                  <span className="block mb-2 text-xs font-mono uppercase tracking-[0.18em] text-stone-400">
                    Attrezzature di Interesse
                  </span>
                  <div data-testid="quote-equipment-checkboxes" className="flex flex-wrap gap-2">
                    {EQUIPMENT_OPTIONS.map((opt) => {
                      const on = equipment.includes(opt);
                      return (
                        <button
                          key={opt}
                          type="button"
                          data-testid={`quote-equip-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          onClick={() => toggleEquipment(opt)}
                          aria-pressed={on}
                          className={`px-4 py-2 text-xs rounded-full border transition-all duration-300 min-h-[44px] ${
                            on
                              ? "bg-amber-400 text-[#181410] border-amber-400 font-medium"
                              : "border-amber-500/20 text-stone-300 hover:border-amber-400/50"
                          }`}
                        >
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <Field label="Dettagli o richieste particolari">
                  <textarea
                    data-testid="quote-notes-input"
                    value={form.notes}
                    onChange={set("notes")}
                    rows={4}
                    placeholder="N. invitati, orari, planimetrie, esigenze particolari..."
                    className={`${inputCls} resize-none`}
                  />
                </Field>
                <button
                  data-testid="quote-submit-btn"
                  type="submit"
                  disabled={sending}
                  className="w-full py-4 rounded-full bg-amber-400 text-[#181410] text-sm font-semibold hover:bg-amber-300 disabled:opacity-60 transition-all duration-300 flex items-center justify-center gap-2"
                >
                  {sending && <Loader2 className="w-4 h-4 animate-spin" />}
                  {sending ? "Invio in corso..." : "Invia Richiesta Preventivo"}
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
